import React, {
  ChangeEvent,
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  ArrowRight,
  Loader2,
  Mic,
  Paperclip,
  Smile,
  RotateCcw,
  SendHorizonal,
  Square,
} from "lucide-react"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { languages } from "@/lib/languages"
import { useTranslation } from "react-i18next"
import i18n from "@/lib/i18n/i18n"
import { selectTranslationPair } from "@/reducers/accountChatsSelectors"
import { XmppContext } from "../stream/xmppClient"
import {
  clearTranslationFailure,
  selectTranslationFailure,
  selectTranslationPaused,
  toggleTranslationPaused,
} from "@/reducers/composerTranslationSlice"
import { Chat } from "@/reducers/chatsSlice"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { driversProfiles } from "@/reducers/profilesSliceSelectors"
import { Profile } from "@/reducers/profilesSlice"
import ChatInputFiles, { PastedFile } from "./ChatInputFiles"
import { shallowEqual } from "react-redux"
import { isMobile } from "@/utils/detectDevice"
import ChatConnectionStatusBar from "./ChatConnectionStatusBar"
import { ChatReplyBar } from "./ChatReplyBar"
import { clearReplyMessage } from "@/reducers/chatRepliesSlice"
import type { ComposerSurface } from "@/reducers/chatRepliesSlice"
import { selectChatReplyMessage } from "@/reducers/chatRepliesSliceSelectors"
import {
  clearComposerDraft,
  selectComposerDraft,
} from "@/reducers/composerDraftsSlice"
import { isRoomFailed } from "@/reducers/roomSyncStatusSelectors"
import usePromptSuggestions from "./slickAI/hooks/usePromptSuggestions"
import { MessageOptions } from "@/reducers/chatsSliceThunks"
import useRecordAudioMessage from "../hooks/useRecordAudioMessage"
import VoiceRecordingBar from "./VoiceRecordingBar"
import MicrophonePermissionOverlay from "./MicrophonePermissionOverlay"
import MicrophonePermissionRequest from "./MicrophonePermissionRequest"
import { selectLocation } from "@/reducers/locationsSelectors"
import { selectLocationJidFromChatJid } from "@/reducers/selectors"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  EmojiPicker,
  EmojiPickerContent,
  EmojiPickerFooter,
  EmojiPickerSearch,
} from "@/components/ui/emoji-picker"
import { useIsDriverWorkspace } from "@/contexts/DriverWorkspaceContext"
import { cn, stripInvalidXMLChars } from "@/lib/utils"
import AnalyzeOrder from "@/components/maps/AnalyzeOrder"
import { AnalyzeOrderProvider } from "@/contexts/AnalyzeOrderContext"
import { useMediaQuery } from "@react-hookz/web"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import ChatHelpers from "@/lib/chatHelpers"
import { isTranschatRoomJid } from "@/lib/privateAgentUtils"
import {
  selectAccountChatByJid,
  selectAccountChats,
} from "@/reducers/accountChatsSlice"

const MAX_COMPOSER_HEIGHT = isMobile ? 216 : 189

/** Defined at module scope on purpose: a component declared inside the render
 * body gets a new identity every render and remounts the whole popover. */
const EmojiPopover = ({
  open,
  onOpenChange,
  onEmojiSelect,
  disabled,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onEmojiSelect: (emoji: { emoji: string }) => void
  disabled?: boolean
}) => (
  <Popover open={open} onOpenChange={onOpenChange}>
    <PopoverTrigger asChild>
      <Button
        variant="ghost"
        size="icon"
        disabled={disabled}
        className="h-8 w-8"
      >
        <Smile className="h-4 w-4" />
      </Button>
    </PopoverTrigger>
    <PopoverContent className="w-auto p-0" side="top">
      <EmojiPicker className="h-80" onEmojiSelect={onEmojiSelect}>
        <EmojiPickerSearch />
        <EmojiPickerContent />
        <EmojiPickerFooter />
      </EmojiPicker>
    </PopoverContent>
  </Popover>
)

type ChatInputProps = {
  chat: Chat
  onSend: (text: string, files: FileList, options?: MessageOptions) => void
  /** Present in the driver workspace: the composer asks Slick AI instead of
   * writing to the driver. The agent thread carries text only, so in agent mode
   * the composer drops the mic and the paperclip rather than handing media to
   * `onSend`, which the driver sees. Analyze Order deliberately stays on
   * `onSend`. Passing this is what puts the composer in agent mode, so the two
   * can never disagree. */
  onSendAgent?: (text: string) => void
  /** When true, use the AI-assistant placeholder (the "#" tip moves to a hint
   * below the composer). Provided by ChatHistory, gated on the FTUE flag. */
  useAssistantPlaceholder?: boolean
  /** Defaults to true. Set false for secondary composers (e.g. the driver-chat
   * widget) so they don't steal focus from the primary one. */
  autoFocus?: boolean
  /** Renders one line with a labelled black send button ("Send to Gosia")
   * instead of the growing textarea and its icon-only send. The driver-chat
   * widget uses it so it never reads as the Slick AI composer. */
  sendLabel?: string
}

export type ChatInputTextAreaRef = {
  focus: () => void
  value(): string
  setText(text: string): void
}

type AutocompleteItem = {
  id: string
  primaryText: string
  secondaryText?: string
  insertText?: string
  analyticsEvent?: string
  /** When set, picking this item switches the composer's layer mode instead
   * of inserting text — how "#" doubles as a hands-off way to pick "Message
   * {driver}" / "Slick AI" without touching the tabs above the textarea. */
  onSelect?: () => void
}

const driverPhoneAndPlates = (driver: Profile) => {
  return [driver.phone, driver.plates]
    .filter((v) => v && v.length > 0)
    .join(" ")
}

const ChatInput = forwardRef<ChatInputTextAreaRef, ChatInputProps>(
  (
    {
      onSend,
      onSendAgent,
      chat,
      useAssistantPlaceholder = false,
      autoFocus = true,
      sendLabel,
    }: ChatInputProps,
    ref: ForwardedRef<ChatInputTextAreaRef>,
  ) => {
    const { isForwardingMode } = useContext(ChatHistoryContext)

    const { t } = useTranslation()
    const inputFileRef = useRef<HTMLInputElement>(null)
    const inputTextRef = useRef<HTMLTextAreaElement>(null)
    const [value, setValue] = useState<string>("")
    const [isAutocompleteOpen, setAutocompleteOpen] = useState<boolean>(false)
    const drivers = useAppSelector(driversProfiles, shallowEqual)
    const chats = useAppSelector((state) => state.chats, shallowEqual)
    const accountChats = useAppSelector(selectAccountChats, shallowEqual)
    const dispatch = useAppDispatch()
    const [autocompleteStartIndex, setAutocompleteStartIndex] =
      useState<number>(0)
    const [autocompleteActiveItemIndex, setAutocompleteActiveItemIndex] =
      useState<number>(0)
    const activeItemRef = useRef<HTMLDivElement>(null)

    const [pastedFiles, setPastedFiles] = useState<PastedFile[]>([])
    const [isSending, setIsSending] = useState<boolean>(false)
    const [showMicPermissionRequest, setShowMicPermissionRequest] =
      useState<boolean>(false)

    // Only room messages to drivers are translated on send, so the badge must
    // not appear on one-to-one chats or the Slick AI assistant chat — there it
    // would promise a translation that never happens. The languages come from
    // the dispatcher's and the driver's profiles and the pair is undefined when
    // they match, so the badge doubles as an honest indicator: if it is showing,
    // the next message really will be translated.
    const translationFlagOn = Boolean(
      useFeatureFlagEnabled("outgoing-message-translation"),
    )
    const { myJid } = useContext(XmppContext)
    // shallowEqual: the selector returns a fresh object each call.
    const translationPair = useAppSelector(
      selectTranslationPair(chat.jid, myJid?.bare().toString()),
      shallowEqual,
    )
    const translationLanguage =
      translationFlagOn && chat.room && translationPair
        ? languages.find((language) => language.code === translationPair.target)
        : undefined
    const sourceLanguage = languages.find(
      (language) => language.code === translationPair?.source,
    )
    const sourceLanguageName = sourceLanguage?.name ?? translationPair?.source

    const translationPaused = useAppSelector(selectTranslationPaused(chat.jid))
    const translationFailure = useAppSelector(
      selectTranslationFailure(chat.jid),
    )
    const toggleTranslation = useCallback(() => {
      dispatch(toggleTranslationPaused({ chatJid: chat.jid }))
    }, [dispatch, chat.jid])

    /**
     * Re-send a message whose translation failed. `skipTranslation` is what
     * separates the two answers: "Try again" goes back through the translate
     * service, "Send untranslated" deliberately bypasses it.
     */
    const resendFailed = useCallback(
      (skipTranslation: boolean) => {
        if (!translationFailure) return
        const { text, options: failedOptions } = translationFailure
        dispatch(clearTranslationFailure({ chatJid: chat.jid }))
        onSend(text, new DataTransfer().files, {
          ...failedOptions,
          skipTranslation,
        })
      },
      [dispatch, chat.jid, translationFailure, onSend],
    )

    const composerSurface: ComposerSurface = onSendAgent ? "agent" : "driver"
    const replyMessage = useAppSelector(
      selectChatReplyMessage(chat, composerSurface),
    )
    const roomSyncFailed = useAppSelector(isRoomFailed(chat.jid))
    const accountChat = useAppSelector(selectAccountChatByJid(chat.jid))
    const whatsappUnreachable = accountChat?.whatsappInboundRequired === true

    const chatNotConnected =
      roomSyncFailed ||
      whatsappUnreachable ||
      (chat.connectionStatus &&
        ["disconnected", "failed"].includes(chat.connectionStatus))

    const [options, setOptions] = useState<MessageOptions>({})

    const promptsEnabled = false

    const {
      isPromptAutocompleteOpen,
      promptSuggestionsPopoverRenderer,
      topicButtonsRenderer,
    } = usePromptSuggestions(inputTextRef, setValue, setAutocompleteOpen)

    const {
      isRecording,
      isPaused,
      isProcessing,
      hasError,
      error,
      audioStream,
      recordedAudioUrl,
      startRecording,
      pauseRecording,
      resumeRecording,
      stopRecording,
      cancelRecording,
      clearError,
      createPreviewForPlayback,
    } = useRecordAudioMessage()

    // Handler for sending voice messages directly
    const handleSendVoiceMessage = async () => {
      // Stop recording and wait for voice message file
      stopRecording()

      // Listen for the voice message completion event
      const handleVoiceMessageForSending = (
        event: CustomEvent<{ file: File }>,
      ) => {
        const voiceFile = event.detail.file
        const fileList = new DataTransfer()
        fileList.items.add(voiceFile)

        // Send the voice message directly without adding to pastedFiles
        onSend("", fileList.files)

        // Remove the event listener
        window.removeEventListener(
          "voice-message-complete",
          handleVoiceMessageForSending as EventListener,
        )
      }

      // Add temporary event listener for this specific send operation
      window.addEventListener(
        "voice-message-complete",
        handleVoiceMessageForSending as EventListener,
      )
    }

    // Create a handler for the microphone button
    const handleMicrophoneClick = async () => {
      if (isRecording || isPaused) {
        stopRecording()
      } else {
        // Check if we already have microphone permission
        try {
          const permissionResult = await navigator.permissions.query({
            name: "microphone" as PermissionName,
          })

          if (permissionResult.state === "granted") {
            // Permission already granted, start recording directly
            startRecording()
          } else if (permissionResult.state === "denied") {
            // Permission was denied, show permission request to guide user
            setShowMicPermissionRequest(true)
          } else {
            // Permission is in prompt state, show permission request
            setShowMicPermissionRequest(true)
          }
        } catch (error) {
          // Fallback for browsers that don't support permissions.query for microphone
          // Try to access microphone directly and handle the result
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              audio: true,
            })
            stream.getTracks().forEach((track) => track.stop()) // Clean up test stream
            startRecording() // Permission granted, start recording
          } catch (micError) {
            // Permission denied or error, show permission request
            setShowMicPermissionRequest(true)
          }
        }
      }
    }

    const handlePermissionGranted = () => {
      setShowMicPermissionRequest(false)
      startRecording()
    }

    const handlePermissionRequestClose = () => {
      setShowMicPermissionRequest(false)
    }

    // Handle ESC key to stop recording
    useEffect(() => {
      const handleKeyDown = (event: Event) => {
        const keyboardEvent = event as unknown as KeyboardEvent
        if (keyboardEvent.key === "Escape" && (isRecording || isPaused)) {
          cancelRecording()
        }
      }

      document.addEventListener("keydown", handleKeyDown)
      return () => {
        document.removeEventListener("keydown", handleKeyDown)
      }
    }, [isRecording, isPaused, cancelRecording])

    const emptyHeight = sendLabel ? "36px" : "56px"

    // Use a callback for handling height adjustments
    const adjustTextareaHeight = useCallback(
      (element: HTMLTextAreaElement | null) => {
        if (!element) return

        // Chrome counts a wrapped placeholder in scrollHeight, so an empty
        // one-line composer would grow; overflow-hidden hides the same wrap.
        if (sendLabel && !element.value) {
          element.style.height = emptyHeight
          textareaHeight.current = emptyHeight
          return
        }
        element.style.height = "auto"
        const newHeight = Math.min(element.scrollHeight, MAX_COMPOSER_HEIGHT)
        element.style.height = `${newHeight}px`
        textareaHeight.current = `${newHeight}px`
      },
      [sendLabel, emptyHeight],
    )

    useEffect(() => {
      setValue(chat.draftMessage || "")
      setPastedFiles([])

      // Auto-grow textarea when value changes, but only if there's a draft message
      if (inputTextRef.current) {
        const hasDraftMessage =
          chat.draftMessage && chat.draftMessage.trim().length > 0

        if (hasDraftMessage) {
          adjustTextareaHeight(inputTextRef.current)
        } else {
          // Reset to initial height when no draft message
          inputTextRef.current.style.height = emptyHeight
          textareaHeight.current = emptyHeight
        }
      }
    }, [chat.jid, chat.draftMessage, adjustTextareaHeight, emptyHeight])

    const composerDraft = useAppSelector(
      selectComposerDraft(chat.jid, composerSurface),
    )
    useEffect(() => {
      if (!composerDraft) return
      setValue(composerDraft.text)
      setOptions(composerDraft.options)
      setTimeout(() => {
        adjustTextareaHeight(inputTextRef.current)
        inputTextRef.current?.focus()
      }, 100)
      dispatch(
        clearComposerDraft({ chatJid: chat.jid, surface: composerSurface }),
      )
    }, [
      composerDraft,
      chat.jid,
      composerSurface,
      adjustTextareaHeight,
      dispatch,
    ])

    useEffect(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) {
            activeItemRef.current?.scrollIntoView()
          }
        },
        { threshold: 1.0 },
      )

      const observerTargetCurrent = activeItemRef.current

      if (observerTargetCurrent) {
        observer.observe(activeItemRef.current)
      }

      return () => {
        if (observerTargetCurrent) {
          observer.unobserve(observerTargetCurrent)
        }
      }
    }, [autocompleteActiveItemIndex])

    useImperativeHandle(ref, () => {
      return {
        focus: () => {
          inputTextRef.current?.focus()
        },
        value: () => {
          return inputTextRef.current?.value || ""
        },
        setText: (text: string) => {
          setValue(text)
          // The first "#" is the driver-mention placeholder (it can sit in the
          // middle of the prompt, e.g. "Gdzie jest # i co teraz robi?"). Prime
          // the autocomplete at that "#" and put the caret right after it, so
          // the filter is empty (full driver list) and picking a driver inserts
          // it at the placeholder while keeping the rest of the sentence.
          const hashIndex = text.indexOf("#")
          if (hashIndex !== -1) {
            setAutocompleteStartIndex(hashIndex)
          }
          setTimeout(() => {
            const el = inputTextRef.current
            if (!el) return
            adjustTextareaHeight(el)
            el.focus()
            const caret = hashIndex !== -1 ? hashIndex + 1 : text.length
            el.setSelectionRange(caret, caret)
            if (hashIndex !== -1) {
              setAutocompleteOpen(true)
            }
          }, 0)
        },
      }
    }, [adjustTextareaHeight])

    const handleOnChange = useCallback(
      (event: ChangeEvent<HTMLTextAreaElement>) => {
        // if # was removed, close autocomplete
        if (isAutocompleteOpen) {
          if (event.target.value[autocompleteStartIndex] !== "#") {
            setAutocompleteOpen(false)
          }
        }
        setValue(stripInvalidXMLChars(event.target.value))

        // Auto-grow textarea
        adjustTextareaHeight(event.target)
      },
      [adjustTextareaHeight, autocompleteStartIndex, isAutocompleteOpen],
    )

    const isAiMode = composerSurface === "agent"

    const handleOnSend = useCallback(async () => {
      inputTextRef.current?.focus()
      setIsSending(true)
      const fileList = new DataTransfer()
      pastedFiles.forEach((file) => {
        fileList.items.add(file.file)
      })
      try {
        if (onSendAgent) {
          onSendAgent(value)
        } else {
          await onSend(value, fileList.files, options)
        }
        setPastedFiles([])
        setValue("")
        dispatch(
          clearReplyMessage({ chatJid: chat.jid, surface: composerSurface }),
        )
      } finally {
        setIsSending(false)
        setOptions({})
        setTimeout(() => adjustTextareaHeight(inputTextRef.current), 100)
      }
    }, [
      adjustTextareaHeight,
      chat.jid,
      composerSurface,
      dispatch,
      onSend,
      onSendAgent,
      options,
      pastedFiles,
      value,
    ])

    const handleOnEnter = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (chatNotConnected) return
        if (isMobile) return
        if (isSending) return // Prevent multiple sends if already sending
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault()
          handleOnSend()
        }
      },
      [chatNotConnected, handleOnSend, isSending],
    )

    const openFileBrowser = () => {
      inputFileRef.current?.click()
    }

    const updateFilesStates = useCallback(
      (files: File[]) => {
        // The agent thread takes text only; queueing a file here would leave it
        // to be silently dropped by handleOnSend's agent branch.
        if (isAiMode) return

        const maxFiles = 8
        if (pastedFiles.length >= maxFiles) {
          window.appToaster.show({
            message: i18n.t("app.chatInput.tooManyFiles", { max: maxFiles }),
            intent: "danger",
            timeout: 3000,
          })
          window.analytics.track("AttachedFilesLimitReached", {
            pasteAttempt: files.length + maxFiles,
            limit: maxFiles,
          })
          return
        }

        const remainingSpace = maxFiles - pastedFiles.length
        const filesToAdd = files.slice(0, remainingSpace)

        const newFiles = filesToAdd.map((file) => ({
          file,
          url: URL.createObjectURL(file),
        }))

        setPastedFiles((prevFiles) => {
          const newFilesArray = [...prevFiles, ...newFiles]
          return newFilesArray.slice(0, maxFiles)
        })
      },
      [isAiMode, pastedFiles.length],
    )

    const onFileChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return

        const files = Array.from(e.target.files)
        updateFilesStates(files)
      },
      [updateFilesStates],
    )

    const handleOnPaste = useCallback(
      (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (e.clipboardData.files && e.clipboardData.files.length !== 0) {
          e.preventDefault()
          const files = Array.from(e.clipboardData.files)
          updateFilesStates(files)
        }
      },
      [updateFilesStates],
    )

    const handleRemoveImage = useCallback(
      (index: number) => {
        const updatedFiles = pastedFiles.filter((file, i) => i !== index)
        setPastedFiles(updatedFiles)
      },
      [pastedFiles],
    )

    // The workspace composer has no emoji picker. The classic one keeps it.
    const inWorkspace = useIsDriverWorkspace()
    const [isEmojiPickerOpen, setEmojiPickerOpen] = useState<boolean>(false)

    const handleEmojiSelect = useCallback(
      (emoji: { emoji: string }) => {
        setEmojiPickerOpen(false)

        if (inputTextRef.current) {
          const [start, end] = [
            inputTextRef.current.selectionStart,
            inputTextRef.current.selectionEnd,
          ]
          const newString =
            value.substring(0, start ? start : 0) +
            emoji.emoji +
            value.substring(end ? end : 0)
          setValue(newString)
        } else {
          setValue(value + emoji.emoji)
        }
      },
      [value],
    )

    const shouldAutocompleteBookmarkedRooms =
      ChatHelpers.isChatWithAssistant(chat) || isTranschatRoomJid(chat.jid)

    const autocompleteSourceItems = useMemo<AutocompleteItem[]>(() => {
      if (shouldAutocompleteBookmarkedRooms) {
        return [
          ...chats
            .filter(
              (room) =>
                room.room &&
                room.jid !== chat.jid &&
                accountChats[room.jid]?.group === "driver",
            )
            .map((room) => ({
              id: room.jid,
              primaryText: room.name || room.jid,
              insertText: room.name || room.jid,
              analyticsEvent: "BookmarkedRoomAutocompleted",
            })),
        ]
      }

      return [
        ...drivers.map((driver) => {
          const secondaryText = driverPhoneAndPlates(driver)

          return {
            id: driver.jid,
            primaryText: driver.name,
            secondaryText,
            insertText: [driver.name, secondaryText]
              .filter((v) => v && v.length > 0)
              .join(" "),
            analyticsEvent: "DriverInfoAutocompleted",
          }
        }),
      ]
    }, [
      accountChats,
      chats,
      chat.jid,
      drivers,
      shouldAutocompleteBookmarkedRooms,
    ])

    const filteredAutocompleteItems = useMemo(() => {
      if (!inputTextRef.current) return []
      const filterValue = value.slice(
        autocompleteStartIndex + 1,
        inputTextRef.current?.selectionStart,
      )

      // prevent flickering of menu due to delay with state updates
      if (filterValue === "#" || filterValue === "") {
        return autocompleteSourceItems
      }

      const itemsInAutocomplete = autocompleteSourceItems.filter((item) =>
        shouldAutocompleteBookmarkedRooms
          ? item.primaryText.toLowerCase().includes(filterValue.toLowerCase())
          : [item.primaryText, item.secondaryText]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()
              .includes(filterValue.toLowerCase()),
      )

      setAutocompleteActiveItemIndex(0)
      return itemsInAutocomplete
    }, [
      autocompleteSourceItems,
      autocompleteStartIndex,
      shouldAutocompleteBookmarkedRooms,
      value,
    ])

    const renderAutocompleteContent = () => {
      if (!isAutocompleteOpen) return <></>

      if (filteredAutocompleteItems.length === 0) {
        setAutocompleteOpen(false)
        return <></>
      }

      return (
        <div className="max-h-48 overflow-y-auto rounded-md border border-border bg-background shadow-md">
          {filteredAutocompleteItems.map((item, index) => (
            <div
              key={item.id}
              ref={
                index === autocompleteActiveItemIndex
                  ? activeItemRef
                  : undefined
              }
              className={cn(
                "cursor-pointer px-3 py-2 hover:bg-accent hover:text-accent-foreground",
                index === autocompleteActiveItemIndex &&
                  "bg-accent text-accent-foreground",
              )}
              onClick={() => {
                insertAutocompleteItem(item)
                setAutocompleteOpen(false)
              }}
            >
              <span className="font-semibold">{item.primaryText}</span>
              {item.secondaryText && (
                <>
                  {" "}
                  <span className="text-muted-foreground">
                    {item.secondaryText}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )
    }

    const insertAutocompleteItem = useCallback(
      (item: AutocompleteItem) => {
        if (item.onSelect) {
          // Mode-switch entries don't insert anything — just clean out the
          // "#filter" text that triggered the picker and flip the mode.
          setValue((currentValue) => {
            const leftPart = currentValue.slice(0, autocompleteStartIndex)
            const rightPart = currentValue.slice(
              inputTextRef.current?.selectionStart ?? currentValue.length,
              currentValue.length,
            )
            return leftPart + rightPart
          })
          inputTextRef.current?.focus()
          item.onSelect()
          return
        }

        setValue((currentValue) => {
          const leftPart = currentValue.slice(0, autocompleteStartIndex)
          const spaceBeforeIfNeeded =
            autocompleteStartIndex !== 0 &&
            currentValue[autocompleteStartIndex - 1] !== " "
              ? " "
              : ""
          const spaceAfterIfNeeded =
            inputTextRef.current &&
            currentValue[inputTextRef.current.selectionStart] !== " "
              ? " "
              : ""
          const rightPart = currentValue.slice(
            inputTextRef.current?.selectionStart,
            currentValue.length,
          )

          return (
            leftPart +
            spaceBeforeIfNeeded +
            item.insertText +
            spaceAfterIfNeeded +
            rightPart
          )
        })

        inputTextRef.current?.focus()
        if (item.analyticsEvent) window.analytics.track(item.analyticsEvent)
      },
      [autocompleteStartIndex],
    )

    const handleOnKeyDownCapture = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if ((e.key === "Tab" || e.key === "Enter") && isAutocompleteOpen) {
          e.preventDefault()
          e.stopPropagation()

          // Use functional updates where possible to avoid dependencies
          const selectedItem =
            filteredAutocompleteItems[autocompleteActiveItemIndex]
          if (!selectedItem) return
          insertAutocompleteItem(selectedItem)
          setAutocompleteOpen(false)
        }

        if (e.key === "Escape" && isAutocompleteOpen) {
          e.preventDefault()
          e.stopPropagation()
          setAutocompleteOpen(false)
        }

        if (e.key === "ArrowDown" && isAutocompleteOpen) {
          e.preventDefault()
          e.stopPropagation()

          setAutocompleteActiveItemIndex((prevIndex) => {
            return prevIndex === filteredAutocompleteItems.length - 1
              ? 0
              : prevIndex + 1
          })
        }

        if (e.key === "ArrowUp" && isAutocompleteOpen) {
          e.preventDefault()
          e.stopPropagation()

          setAutocompleteActiveItemIndex((prevIndex) => {
            return prevIndex === 0
              ? filteredAutocompleteItems.length - 1
              : prevIndex - 1
          })
        }
      },
      [
        autocompleteActiveItemIndex,
        isAutocompleteOpen,
        filteredAutocompleteItems,
        insertAutocompleteItem,
      ],
    )

    const handleOnKeyUpCapture = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (
          value[e.currentTarget.selectionStart - 1] === "#" &&
          e.key !== "Escape"
        ) {
          setAutocompleteStartIndex(e.currentTarget.selectionStart - 1)
          if (!isAutocompleteOpen && !isPromptAutocompleteOpen) {
            setAutocompleteOpen(true)
          }
        }
      },
      [isAutocompleteOpen, isPromptAutocompleteOpen, value],
    )

    const textareaHeight = useRef<string>("56px")

    const textareaStyle = useMemo(
      () => ({
        fontSize: isMobile ? "16px" : undefined,
        height: textareaHeight.current,
        minHeight: "56px",
        maxHeight: `${MAX_COMPOSER_HEIGHT}px`,
        lineHeight: "1.5",
      }),
      [],
    )

    const locationJid = useAppSelector(selectLocationJidFromChatJid(chat.jid))
    const currentLocation = useAppSelector(selectLocation(locationJid))
    const isSmallScreen = useMediaQuery("(max-width: 900px)")
    const showAnalyzeOrderButton =
      currentLocation && !isSmallScreen && !isMobile

    if (whatsappUnreachable) {
      return (
        <div className="relative flex flex-col">
          <ChatConnectionStatusBar
            status={chat.connectionStatus}
            whatsappUnreachable={whatsappUnreachable}
          />
        </div>
      )
    }
    const textareaElement = (
      <Textarea
        data-testid="chat-input"
        data-composer={isAiMode ? "agent" : "driver"}
        onPaste={handleOnPaste}
        tabIndex={0}
        autoFocus={autoFocus && !isMobile}
        onChange={handleOnChange}
        value={value}
        // Locked while the send is in flight so the dispatcher cannot type
        // over text that has not left yet. The text stays visible.
        //
        // readOnly, NOT disabled: a disabled control cannot hold focus, so the
        // browser moves focus to <body> the moment this flips true — and
        // nothing brings it back when it flips false again. That cost the
        // dispatcher the caret on every single send (reported 2026-09-29:
        // "zeby kontynuowac pisanie musze znow kliknac na okienko"). readOnly
        // blocks typing just the same while the field keeps focus and caret.
        readOnly={isSending}
        aria-label="chat-input"
        onKeyDown={handleOnEnter}
        placeholder={
          isAiMode
            ? t("app.chatInput.placeholderSlickAI")
            : promptsEnabled
              ? ""
              : useAssistantPlaceholder
                ? t("app.chatInput.placeholderAssistant")
                : shouldAutocompleteBookmarkedRooms
                  ? t("app.chatInput.placeholderRooms")
                  : t("app.chatInput.placeholderDriver")
        }
        ref={inputTextRef}
        onKeyDownCapture={handleOnKeyDownCapture}
        onKeyUpCapture={handleOnKeyUpCapture}
        rows={sendLabel ? 1 : undefined}
        className={cn(
          "font-inherit resize-none overflow-y-auto text-base leading-normal",
          // The workspace draws one card around the textarea and the toolbar,
          // so the textarea drops its own frame. On its own it keeps it.
          inWorkspace || sendLabel
            ? "border-none bg-transparent shadow-none"
            : pastedFiles.length !== 0 && "border-none shadow-none",
          sendLabel && "py-1.5",
          sendLabel && !value && "overflow-hidden",
          isForwardingMode && "invisible",
        )}
        style={
          sendLabel
            ? {
                minHeight: "36px",
                maxHeight: `${MAX_COMPOSER_HEIGHT}px`,
              }
            : textareaStyle
        }
      />
    )

    return (
      <div className="relative flex flex-col">
        <ChatConnectionStatusBar status={chat.connectionStatus} />

        {/* Nothing was sent. The dispatcher decides whether their own
            language goes in front of the driver, so this bar has no
            auto-dismiss and no timeout — it waits. */}
        {translationFailure && (
          <div
            role="alert"
            data-testid="translation-failure-bar"
            className="mb-2 flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm"
          >
            <span className="flex-1 text-destructive">
              {t("app.chatInput.translationFailed")}
            </span>
            {/* Gated on isSending like every other control here. Without it
                a dispatcher could retry this while a newer message is still
                waiting on the translate lambda, putting two sends to the same
                chat in flight at once. */}
            <Button
              size="sm"
              variant="default"
              disabled={chatNotConnected || isSending}
              onClick={() => resendFailed(false)}
            >
              {t("app.actions.tryAgain")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={chatNotConnected || isSending}
              onClick={() => resendFailed(true)}
            >
              {t("app.chatInput.sendUntranslated")}
            </Button>
          </div>
        )}

        {replyMessage && (
          <ChatReplyBar
            replyMessage={replyMessage}
            onClearReply={() =>
              dispatch(
                clearReplyMessage({
                  chatJid: chat.jid,
                  surface: composerSurface,
                }),
              )
            }
          />
        )}

        <Popover open={isAutocompleteOpen} onOpenChange={setAutocompleteOpen}>
          <PopoverTrigger asChild>
            <div className="h-px w-full" />
          </PopoverTrigger>
          <PopoverContent
            className="w-full p-0"
            side="top"
            align="start"
            sideOffset={5}
            onOpenAutoFocus={(e) => {
              e.preventDefault()
              inputTextRef.current?.focus()
            }}
            onCloseAutoFocus={(e) => {
              e.preventDefault()
              inputTextRef.current?.focus()
            }}
          >
            {renderAutocompleteContent()}
          </PopoverContent>
        </Popover>

        {promptsEnabled && promptSuggestionsPopoverRenderer()}

        <div
          className={cn(
            "flex flex-col",
            (inWorkspace || sendLabel) &&
              "rounded-md border shadow-sm transition-colors",
            (inWorkspace || sendLabel) &&
              "border-input bg-white dark:bg-input/30",
            sendLabel && "gap-1.5 px-2 py-1.5",
          )}
        >
          {(isRecording || isPaused) && (
            <VoiceRecordingBar
              isRecording={isRecording}
              isPaused={isPaused}
              audioStream={audioStream}
              recordedAudioUrl={recordedAudioUrl}
              onPause={pauseRecording}
              onResume={resumeRecording}
              onStop={stopRecording}
              onCancel={cancelRecording}
              onSend={handleSendVoiceMessage}
              createPreviewForPlayback={createPreviewForPlayback}
            />
          )}
          {!isRecording && !isPaused && !sendLabel && textareaElement}

          {pastedFiles.length !== 0 && !isRecording && !isPaused && (
            <ChatInputFiles
              pastedFiles={pastedFiles}
              handleRemoveImage={handleRemoveImage}
            />
          )}

          <div
            className={cn(
              sendLabel
                ? "flex flex-row items-end gap-1.5"
                : inWorkspace
                  ? "flex flex-row items-center gap-2 px-2 pb-2"
                  : "flex flex-row items-center gap-2 py-2",
              (isRecording || isProcessing || isPaused) &&
                (sendLabel ? "hidden" : "invisible"),
            )}
          >
            {sendLabel && textareaElement}
            {showAnalyzeOrderButton && !sendLabel && (
              <AnalyzeOrderProvider>
                <AnalyzeOrder
                  onSend={onSend}
                  showButton
                  disabled={
                    chatNotConnected ||
                    isSending ||
                    isRecording ||
                    isProcessing ||
                    isPaused
                  }
                />
              </AnalyzeOrderProvider>
            )}
            <div className="flex flex-1 flex-row gap-2">
              {promptsEnabled && topicButtonsRenderer()}
            </div>

            {/* The agent thread carries text only, so the Slick AI composer
                has no media controls at all: anything recorded or attached
                here would go to the driver instead. */}
            {!isAiMode && (
              <>
                {translationLanguage && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleTranslation}
                    data-testid="translation-language-badge"
                    aria-pressed={!translationPaused}
                    title={
                      translationPaused
                        ? t("app.chatInput.translationPausedTitle", {
                            source: sourceLanguageName,
                            target: translationLanguage.name,
                          })
                        : t("app.chatInput.translatingTitle", {
                            source: sourceLanguageName,
                            target: translationLanguage.name,
                          })
                    }
                    aria-label={
                      translationPaused
                        ? t("app.chatInput.translationPausedLabel", {
                            source: sourceLanguageName,
                            target: translationLanguage.name,
                          })
                        : t("app.chatInput.translatingLabel", {
                            source: sourceLanguageName,
                            target: translationLanguage.name,
                          })
                    }
                    className="relative h-8 w-auto shrink-0 px-1.5"
                  >
                    {/* From → into, so the dispatcher sees both sides of the
                        translation at a glance, not only the driver's. */}
                    <span
                      className={cn(
                        "flex items-center gap-0.5 text-base leading-none",
                        translationPaused && "opacity-40",
                      )}
                    >
                      <span>{sourceLanguage?.flag ?? sourceLanguageName}</span>
                      <ArrowRight
                        aria-hidden
                        className="h-3 w-3 text-muted-foreground"
                      />
                      <span>{translationLanguage.flag}</span>
                    </span>
                    {translationPaused && (
                      <span
                        aria-hidden
                        className="absolute h-[1.5px] w-10 -rotate-12 rounded-full bg-muted-foreground"
                      />
                    )}
                  </Button>
                )}

                <Button
                  variant="ghost"
                  size="icon"
                  disabled={
                    chatNotConnected ||
                    isSending ||
                    isRecording ||
                    isProcessing ||
                    isPaused
                  }
                  onClick={openFileBrowser}
                  className={cn("h-8 w-8 shrink-0", isMobile && "ml-auto")}
                >
                  <Paperclip className={cn("h-4 w-4", isMobile && "h-5 w-5")} />
                </Button>

                {!window.ReactNativeWebView && (
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={
                      chatNotConnected || isSending || isProcessing || isPaused
                    }
                    onClick={handleMicrophoneClick}
                    className={cn(
                      "h-8 w-8 shrink-0",
                      (isRecording || isPaused) &&
                        "animate-pulse text-destructive",
                    )}
                    data-testid="microphone-button"
                  >
                    {isRecording ? (
                      <Square
                        className={cn("h-4 w-4", isMobile && "h-5 w-5")}
                      />
                    ) : isProcessing ? (
                      <RotateCcw
                        className={cn("h-4 w-4", isMobile && "h-5 w-5")}
                      />
                    ) : (
                      <Mic className={cn("h-4 w-4", isMobile && "h-5 w-5")} />
                    )}
                  </Button>
                )}

                <input
                  data-testid="chat-input-file"
                  type="file"
                  multiple={true}
                  onChange={onFileChange}
                  className="hidden"
                  ref={inputFileRef}
                  onClick={(e) => {
                    e.currentTarget.value = ""
                  }}
                />
              </>
            )}

            {!inWorkspace && !isMobile && (
              <EmojiPopover
                open={isEmojiPickerOpen}
                onOpenChange={setEmojiPickerOpen}
                onEmojiSelect={handleEmojiSelect}
                disabled={
                  chatNotConnected ||
                  isSending ||
                  isRecording ||
                  isPaused ||
                  isProcessing
                }
              />
            )}

            <Button
              variant={sendLabel ? "default" : "ghost"}
              size={sendLabel ? "sm" : "icon"}
              disabled={
                chatNotConnected ||
                isSending ||
                isRecording ||
                isProcessing ||
                isPaused
              }
              onClick={handleOnSend}
              className={cn(
                sendLabel
                  ? "h-8 shrink-0 whitespace-nowrap bg-black text-white hover:bg-black/90"
                  : "h-8 w-8 bg-base-primary text-white",
              )}
              data-testid="send"
            >
              {/* A send can take a while: the translate lambda's cold start
                  was measured at ~10s. Spinning here is the only signal the
                  dispatcher gets that their message is still on its way. */}
              {isSending ? (
                <Loader2
                  data-testid="send-spinner"
                  aria-label={t("app.chatInput.sending")}
                  className={cn("h-4 w-4 animate-spin", isMobile && "h-5 w-5")}
                />
              ) : (
                (sendLabel ?? (
                  <SendHorizonal
                    className={cn("h-4 w-4", isMobile && "h-5 w-5")}
                  />
                ))
              )}
            </Button>
          </div>
        </div>

        <MicrophonePermissionOverlay
          isOpen={hasError}
          error={error}
          onClose={clearError}
          onRetry={startRecording}
        />

        {showMicPermissionRequest && (
          <MicrophonePermissionRequest
            onPermissionGranted={handlePermissionGranted}
            onClose={handlePermissionRequestClose}
          />
        )}
      </div>
    )
  },
)

export default React.memo(ChatInput)
